// Tabs and Accordion.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

/**
 * <i80-tabs>
 *   <i80-tab icon="org" count="3">Members</i80-tab>
 *   <i80-tab selected>Settings</i80-tab>
 *   <i80-tab-panel>…</i80-tab-panel>
 *   <i80-tab-panel>…</i80-tab-panel>
 * </i80-tabs>
 *
 * Tabs and panels are paired by order. As in Helios, the arrow keys move focus between
 * tabs and Enter/Space (or a click) selects one. Fires i80-change with {index}.
 */
export class I80Tabs extends I80Element {
  static get observedAttributes() {
    return ["size", "selected"];
  }

  setup() {
    let root = this.part("tabs");
    if (!root) {
      root = h("div", { "data-i80-part": "tabs" },
        h("div", { class: "i80-tabs__tablist-wrapper" },
          h("ul", { class: "i80-tabs__tablist", role: "tablist" }, h("li", { class: "i80-tabs__tab-indicator", role: "presentation" }))));
      this.append(root);
    }
    this._root = root;
    this.adopt();
    // A clone carries the original's ids; every instance gets its own.
    for (const el of root.querySelectorAll(".i80-tabs__tab-button, :scope > .i80-tabs__panel")) el.removeAttribute("id");
    this._wireIds();
    if (typeof ResizeObserver !== "undefined") {
      this._resize = new ResizeObserver(() => this._indicator());
      this._resize.observe(root.querySelector(".i80-tabs__tablist"));
    }
  }

  /** Turn <i80-tab> and <i80-tab-panel> children into the list and the panels. */
  adopt() {
    const root = this._root;
    const list = root.querySelector(".i80-tabs__tablist");
    const indicator = list.querySelector(".i80-tabs__tab-indicator");
    for (const t of [...this.children].filter((c) => c.localName === "i80-tab")) {
      const button = h("button", { class: "i80-tabs__tab-button", role: "tab", type: "button" },
        t.getAttribute("icon") ? icon(t.getAttribute("icon"), { className: "i80-tabs__tab-icon" }) : null,
        ...t.childNodes,
        t.hasAttribute("count")
          ? h("div", { class: "i80-badge-count i80-badge-count--size-small i80-badge-count--type-filled i80-badge-count--color-neutral i80-tabs__tab-count", role: "presentation" }, t.getAttribute("count"))
          : null);
      if (t.hasAttribute("selected") && !this.hasAttribute("selected")) {
        this._pendingSelected = list.querySelectorAll(".i80-tabs__tab").length;
      }
      indicator.before(h("li", { class: "i80-tabs__tab", role: "presentation" }, button));
      t.remove();
    }
    for (const p of [...this.children].filter((c) => c.localName === "i80-tab-panel")) {
      const section = h("section", { class: "i80-tabs__panel", role: "tabpanel" }, ...p.childNodes);
      for (const a of p.getAttributeNames()) if (a !== "selected") section.setAttribute(a, p.getAttribute(a));
      section.className = "i80-tabs__panel";
      root.append(section);
      p.remove();
    }

    this._buttons = [...root.querySelectorAll(".i80-tabs__tab-button")];
    this._panels = [...root.querySelectorAll(":scope > .i80-tabs__panel")];
    this._wireIds();
    this._buttons.forEach((b) => {
      if (b.dataset.i80Wired) return;
      b.dataset.i80Wired = "1";
      b.addEventListener("click", () => this.select(this._buttons.indexOf(b)));
      b.addEventListener("keydown", (e) => this._key(e, this._buttons.indexOf(b)));
    });
    if (this._pendingSelected > 0 && !this.hasAttribute("selected")) {
      this.setAttribute("selected", String(this._pendingSelected));
      this._pendingSelected = 0;
    }
  }

  _wireIds() {
    this._buttons.forEach((b, i) => {
      if (!b.id) b.id = nextId("tab");
      const panel = this._panels[i];
      if (panel && !panel.id) panel.id = nextId("panel");
      if (panel) {
        b.setAttribute("aria-controls", panel.id);
        panel.setAttribute("aria-labelledby", b.id);
      }
    });
  }

  update() {
    this._root.className = `i80-tabs i80-tabs--size-${this.choice("size", ["medium", "large"], "medium")}`;
    const n = this._buttons.length;
    const index = Math.min(Math.max(parseInt(this.getAttribute("selected") ?? "0", 10) || 0, 0), Math.max(n - 1, 0));
    this._buttons.forEach((b, i) => {
      const on = i === index;
      b.parentElement.className = on ? "i80-tabs__tab i80-tabs__tab--is-selected" : "i80-tabs__tab";
      b.setAttribute("aria-selected", String(on));
      if (on) b.removeAttribute("tabindex");
      else b.setAttribute("tabindex", "-1");
    });
    this._panels.forEach((p, i) => (p.hidden = i !== index));
    this._index = index;
    requestAnimationFrame(() => this._indicator());
  }

  get selectedIndex() {
    return this._index ?? 0;
  }

  select(index) {
    if (index === this._index) return;
    this.setAttribute("selected", String(index));
    this.dispatchEvent(new CustomEvent("i80-change", { bubbles: true, detail: { index } }));
  }

  _key(e, i) {
    const n = this._buttons.length;
    const to = { ArrowRight: (i + 1) % n, ArrowLeft: (i + n - 1) % n, Home: 0, End: n - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    this._buttons[to].focus();
    this._buttons[to].parentElement.scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  _indicator() {
    const li = this._buttons[this._index]?.parentElement;
    if (!li?.offsetParent) return;
    const list = li.parentElement;
    list.style.setProperty("--indicator-left-pos", `${li.offsetLeft}px`);
    list.style.setProperty("--indicator-width", `${li.offsetWidth}px`);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._resize?.disconnect();
  }

  connectedCallback() {
    super.connectedCallback();
    this._resize?.observe(this._root.querySelector(".i80-tabs__tablist"));
  }
}

const TEXT_SIZE = { small: 100, medium: 200, large: 300 };

/**
 * <i80-accordion>
 *   <i80-accordion-item heading="What is a plan?" open>A plan is …</i80-accordion-item>
 * </i80-accordion>
 * Size and type are set on <i80-accordion> (or per item). Fires i80-toggle with {open}.
 */
export class I80Accordion extends I80Element {
  static get observedAttributes() {
    return ["size", "type"];
  }

  setup() {
    let root = this.part("accordion");
    if (!root) {
      root = h("div", { "data-i80-part": "accordion" }, this.takeContent());
      this.append(root);
    }
    this._root = root;
  }

  adopt() {
    this._root.append(...this.takeContent());
  }

  update() {
    this._root.className = `i80-accordion i80-accordion--size-${this.size} i80-accordion--type-${this.type}`;
    for (const item of this._root.querySelectorAll("i80-accordion-item")) item.update?.();
  }

  get size() {
    return this.choice("size", ["small", "medium", "large"], "medium");
  }

  get type() {
    return this.choice("type", ["card", "flush"], "card");
  }
}

export class I80AccordionItem extends I80Element {
  static get observedAttributes() {
    return ["heading", "open", "size", "type", "contains-interactive", "title-tag", "aria-label"];
  }

  setup() {
    let root = this.part("item");
    if (!root) {
      const toggleSlot = [...this.children].filter((c) => c.getAttribute("slot") === "toggle");
      const content = this.takeContent(toggleSlot);
      root = h("div", { "data-i80-part": "item" },
        h("div", { class: "i80-disclosure-primitive__toggle" },
          h("div", { class: "i80-accordion-item__toggle" },
            h("button", { type: "button", "data-i80-part": "button" }),
            h("div", { "data-i80-part": "title" }, toggleSlot))),
        h("div", { class: "i80-disclosure-primitive__content", "data-i80-part": "content" },
          h("div", { class: "i80-text i80-typography-body-200 i80-font-weight-regular i80-foreground-primary i80-accordion-item__content" }, content)));
      this.append(root);
    }
    this._root = root;
    this._button = root.querySelector('[data-i80-part="button"]');
    this._content = root.querySelector('[data-i80-part="content"]');
    this._content.id = nextId("content");
    this._titleId = nextId("title");
    this._button.addEventListener("click", () => this.toggle());
  }

  adopt() {
    const title = this._root.querySelector('[data-i80-part="title"]');
    const content = this._content.firstElementChild;
    for (const node of this.takeContent()) {
      (node.nodeType === 1 && node.getAttribute("slot") === "toggle" ? title : content).append(node);
    }
  }

  update() {
    if (!this._root) return;
    const parent = this.closest("i80-accordion");
    const size = this.choice("size", ["small", "medium", "large"], parent?.size ?? "medium");
    const type = this.choice("type", ["card", "flush"], parent?.type ?? "card");
    const open = this.flag("open");
    const interactive = this.flag("contains-interactive");
    this._root.className = [
      "i80-disclosure-primitive", "i80-accordion-item", open && "i80-accordion-item--is-open",
      `i80-accordion-item--size-${size}`, `i80-accordion-item--type-${type}`,
      interactive ? "i80-accordion-item--contains-interactive" : "i80-accordion-item--does-not-contain-interactive",
    ].filter(Boolean).join(" ");

    const b = this._button;
    b.className = [
      "i80-accordion-item__button", open && "i80-accordion-item__button--is-open", `i80-accordion-item__button--size-${size}`,
      interactive ? "i80-accordion-item__button--parent-contains-interactive" : "i80-accordion-item__button--parent-does-not-contain-interactive",
    ].filter(Boolean).join(" ");
    b.setAttribute("aria-controls", this._content.id);
    b.setAttribute("aria-expanded", String(open));
    const label = this.getAttribute("aria-label");
    if (label) {
      b.setAttribute("aria-label", label);
      b.removeAttribute("aria-labelledby");
    } else {
      b.setAttribute("aria-labelledby", this._titleId);
      b.removeAttribute("aria-label");
    }
    b.replaceChildren(icon("chevron-down", { size: size === "large" ? "24" : "16" }));

    // The title element's tag can change (title-tag), so it is rebuilt around the same nodes.
    const oldTitle = this._root.querySelector('[data-i80-part="title"]');
    const tag = this.choice("title-tag", ["div", "h1", "h2", "h3", "h4", "h5", "h6"], "div");
    const title = oldTitle.localName === tag ? oldTitle : h(tag, { "data-i80-part": "title" }, ...oldTitle.childNodes);
    if (title !== oldTitle) oldTitle.replaceWith(title);
    title.className = `i80-text i80-typography-body-${TEXT_SIZE[size]} i80-font-weight-semibold i80-foreground-strong i80-accordion-item__toggle-content`;
    title.id = this._titleId;
    const heading = this.getAttribute("heading");
    if (heading !== null) title.textContent = heading;

    this._content.firstElementChild.hidden = !open;
  }

  get open() {
    return this.flag("open");
  }

  set open(v) {
    this.toggleAttribute("open", !!v);
  }

  toggle(force) {
    const open = force ?? !this.flag("open");
    this.toggleAttribute("open", open);
    this.dispatchEvent(new CustomEvent("i80-toggle", { bubbles: true, detail: { open } }));
  }
}

define("i80-tabs", I80Tabs);
define("i80-accordion", I80Accordion);
define("i80-accordion-item", I80AccordionItem);
